import { Link, useLocation } from "react-router-dom";
import useIsAdmin from "../../hooks/useIsAdmin";
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  MdClose,
} from "react-icons/md";
import DashboardIcon from "../../assets/icons/sidebar/dashboard_icon.svg?react";
import InstallationsIcon from "../../assets/icons/sidebar/installations_icon.svg?react";
import UsersIcon from "../../assets/icons/sidebar/users_icon.svg?react";
import OrganizationsIcon from "../../assets/icons/sidebar/org_icon.svg?react";
import UsageIcon from "../../assets/icons/sidebar/usage_icon.svg?react";
import FinanceIcon from "../../assets/icons/sidebar/finance_icon.svg?react";
import OperationsIcon from "../../assets/icons/sidebar/operations_icon.svg?react";
import {
  TbSquareChevronLeftFilled,
  TbSquareChevronRightFilled,
} from "react-icons/tb";
import NeuSurface from "../surface/NeuSurface";
import { getNeuSurfaceStyles } from "../../theme/themeHelpers";
import SidebarAuthSection from "../user/SidebarAuthSection";
import LexOneLogoLime from "../../assets/logos/lexone-lime.svg?react";
import LexOneLogoGrey from "../../assets/logos/lexone-grey.svg?react";
import { useAuth } from "react-oidc-context";
import { isUserInGroup } from "../../utils/userUtils";


const expandedWidth = 250;
const collapsedWidth = 92;

function SidebarDivider() {
  return (
    <Box
      sx={(theme) => ({
        height: "1px",
        width: "100%",
        mb: 3,
        background: theme.custom.dividers.taperedLight,
        ...theme.applyStyles("dark", {
          background: theme.custom.dividers.taperedDark,
        }),
      })}
    />
  );
}

function SidebarNavItem({
  label,
  path,
  icon: Icon,
  selected,
  collapsed,
  isMobile,
  onClick,
}) {
  return (
    <ListItemButton
      disableRipple
      component={Link}
      to={path}
      onClick={onClick}
      selected={selected}
      sx={(theme) => ({
        ...(selected
          ? getNeuSurfaceStyles(theme, "inset")
          : getNeuSurfaceStyles(theme, "control")),

        minHeight: 56,
        width: collapsed && !isMobile ? 56 : "100%",
        justifyContent: collapsed && !isMobile ? "center" : "flex-start",
        px: collapsed && !isMobile ? 0 : 1.75,
        mb: 2,
        mx: collapsed && !isMobile ? "auto" : 0,
        color: selected
          ? theme.palette.text.primary
          : theme.palette.text.secondary,
        transition:
          "width 240ms cubic-bezier(0.22, 1, 0.36, 1), padding 240ms cubic-bezier(0.22, 1, 0.36, 1), opacity 160ms ease",

        "& .MuiListItemText-primary": {
          fontWeight: selected ? 600 : 500,
          opacity: collapsed && !isMobile ? 0 : 1,
          transition: "opacity 160ms ease",
          whiteSpace: "nowrap",
        },

        "&:hover": selected
          ? {}
          : {
              boxShadow: `${theme.custom.elevation.control.light}, ${theme.custom.effects.accentRaised}`,
              ...theme.applyStyles("dark", {
                boxShadow: `${theme.custom.elevation.control.dark}, ${theme.custom.effects.accentRaised}`,
              }),
            },
      })}
    >
      <ListItemIcon
        sx={(theme) => ({
          minWidth: collapsed && !isMobile ? 0 : 28,
          mr: collapsed && !isMobile ? 0 : 1.5,
          justifyContent: "center",
          color: selected
            ? theme.palette.primary.main
            : theme.palette.text.secondary,
          fontSize: "1.7rem",
        })}
      >
        <Icon
          style={{
            width: 32,
            height: 32,
            display: "block",
          }}
        />
      </ListItemIcon>

      {!collapsed || isMobile ? <ListItemText primary={label} /> : null}
    </ListItemButton>
  );
}

function SidebarContent({
  sidebarItems,
  location,
  collapsed,
  isMobile,
  onItemClick,
  onClose,
}) {
  const sidebarWidth = collapsed && !isMobile ? collapsedWidth : expandedWidth;

  return (
    <NeuSurface
      variant="panel"
      sx={{
        width: sidebarWidth,
        minWidth: sidebarWidth,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        px: collapsed && !isMobile ? 1.5 : 2.5,
        py: 2.5,
        borderRadius: 0,
        transition:
          "width 240ms cubic-bezier(0.22, 1, 0.36, 1), padding 240ms cubic-bezier(0.22, 1, 0.36, 1)",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", pb: 3 }}>
        <Box
          sx={{

            display: "flex",
            alignItems: "center",
            overflow: "hidden",
            transition: "width 180ms ease",
          }}
        >
          <Box
            component={LexOneLogoGrey}
            aria-label="LexOne"
            sx={(theme) => ({
              width: "100%",
              height: "100%",
              display: "block",
              ...theme.applyStyles("dark", {
                display: "none",
              }),
            })}
          />

          <Box
            component={LexOneLogoLime}
            aria-label="LexOne"
            sx={(theme) => ({
              width: "100%",
              height: "100%",
              display: "none",
              ...theme.applyStyles("dark", {
                display: "block",
              }),
            })}
          />
        </Box>

        {isMobile ? (
          <IconButton onClick={onClose} sx={{ ml: "auto", color: "text.primary" }}>
            <MdClose />
          </IconButton>
        ) : null}
      </Box>

      <SidebarDivider />

      <List disablePadding sx={{ flex: 1 }}>
        {sidebarItems.map(([label, path, icon]) => {
          const selected = location.pathname.startsWith(path);

          return (
            <SidebarNavItem
              key={path}
              label={label}
              path={path}
              icon={icon}
              selected={selected}
              collapsed={collapsed}
              isMobile={isMobile}
              onClick={() => {
                onItemClick?.(label);
                onClose?.();
              }}
            />
          );
        })}
      </List>

      <SidebarDivider />

      <SidebarAuthSection />
    </NeuSurface>
  );
}

const Sidebar = ({
  onItemClick,
  open,
  onClose,
  isMobile,
  collapsed = false,
  onToggleCollapsed,
}) => {
  const location = useLocation();
  const auth = useAuth();
  const isAdmin = useIsAdmin();
  const isDemo = isUserInGroup(auth.user, "LexEnergy-Demo");

  const sidebarItems = [
    ["Dashboard", "/dashboard", DashboardIcon],
    ["Usage", "/usage", UsageIcon ],
    ["Finance", "/finance", FinanceIcon],
    [isDemo ? "Operations" : "Installations", "/installations", InstallationsIcon],
    ["Service Cases", "/service-cases", OperationsIcon],
    isAdmin ? ["Users", "/users", UsersIcon] : null,
    isAdmin ? ["Organizations", "/organizations", OrganizationsIcon] : null,
  ].filter(Boolean);

  if (isMobile) {
    return (
      <Drawer
        anchor="left"
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          "& .MuiDrawer-paper": {
            width: expandedWidth,
            border: "none",
            background: "transparent",
            boxShadow: "none",
          },
        }}
      >
        <SidebarContent
          sidebarItems={sidebarItems}
          location={location}
          collapsed={false}
          isMobile
          onItemClick={onItemClick}
          onClose={onClose}
        />
      </Drawer>
    );
  }

  return (
    <Box sx={{ position: "relative", height: "100vh", flexShrink: 0 }}>
      <SidebarContent
        sidebarItems={sidebarItems}
        location={location}
        collapsed={collapsed}
        isMobile={false}
        onItemClick={onItemClick}
        onClose={onClose}
      />

      {onToggleCollapsed ? (
        <IconButton
          disableRipple
          onClick={onToggleCollapsed}
          sx={(theme) => ({
            position: "absolute",
            top: 40,
            right: -34,
            zIndex: 999,
            width: 72,
            height: 72,
            background: "transparent",
            boxShadow: "none",
            border: "none",
            color: theme.palette.secondary.main,

            ...theme.applyStyles("dark", {
              color: theme.palette.primary.main,
            }),

            "&:hover": {
              background: "transparent",
            },

            "&:active": {
              background: "transparent",
            },
          })}
        >
          {collapsed ? (
            <TbSquareChevronRightFilled />
          ) : (
            <TbSquareChevronLeftFilled />
          )}
        </IconButton>
      ) : null}
    </Box>
  );
};

export default Sidebar;